import { useState } from 'react';
import { Button } from 'nhsuk-react-components';
import usePatchReportReview from 'queries/mutate/patchReportReview';
import { SUMMARY_REPORT_REVIEW_STATUSES } from 'schemas/SummaryReport.schema';
import Confirm from 'components/Confirm';
import Alert from 'components/Alert';
import { IterationDetails } from 'schemas/CampaignConfig.schema.ts';
import { ITERATION_STATUS } from 'schemas/CampaignApiDetails.schema.ts';
import { API_APPROVE_SUMMARY_REPORT_ERROR, API_APPROVE_REPORT_ARCHIVED_ERROR } from 'constants/errorMessages';

export interface IApproveSummaryReportProps {
  teamId: string,
  campaignId: string,
  iterationDetails: IterationDetails,
  batchId: string;
  secondary?: boolean;
}

function ApproveSummaryReport({
  campaignId,
  iterationDetails,
  teamId,
  batchId,
  secondary = false,
}: IApproveSummaryReportProps) {
  const [isShowingApproveDialog, setIsShowingApproveDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const patchReportReview = usePatchReportReview();

  if (!iterationDetails) {
    throw new Error('iterationDetails is required');
  }

  const confirm = async () => {
    try {
      await patchReportReview.mutateAsync({
        campaignId: campaignId!,
        iterationId: iterationDetails.ID!,
        teamId,
        batchId: batchId!,
        summaryReportPatch: {
          SUMMARY_REPORT_REVIEW: { STATUS: SUMMARY_REPORT_REVIEW_STATUSES.report_approved },
        },
      });
    } catch (e) {
      if (e instanceof Error && e.message.includes('stale')) {
        setErrorMessage(API_APPROVE_REPORT_ARCHIVED_ERROR);
        return;
      }
      setErrorMessage(API_APPROVE_SUMMARY_REPORT_ERROR);
    }
  };

  const onClickApprove = () => {
    if (iterationDetails.status === ITERATION_STATUS.REPORT_REJECTED) {
      setIsShowingApproveDialog(true);
      return;
    }

    confirm();
  };

  const abort = () => {
    setIsShowingApproveDialog(false);
  };

  const isSubmitting = patchReportReview.isPending;

  return (
    <>
      <Button
        onClick={onClickApprove}
        secondary={secondary}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Updating...' : 'Approve report'}
      </Button>

      {isShowingApproveDialog && (
        <Confirm
          title="Approve rejected report?"
          message="This summary report was previously rejected. Approving it will change its status to Report approved. Do you want to continue?"
          yesText="Yes, approve report"
          noText="No, return to report"
          onYes={async () => {
            await confirm();
            setIsShowingApproveDialog(false);
          }}
          onNo={abort}
        />
      )}

      {errorMessage && (
        <div className="nhsuk-u-margin-top-3 nhsuk-u-width-full nhsuk-u-padding-4">
          <Alert
            title="Unable to approve report"
            message={errorMessage}
            onClose={() => setErrorMessage(null)}
          />
        </div>
      )}
    </>
  );
}

export default ApproveSummaryReport;
